// ===========================================
// COMPONENTE: ProgramarAlertaDialog (ORR-232)
// ===========================================
// Dialog para programar una alerta sobre un vencimiento puntual

import { useState } from 'react'
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Bell, BellOff, Loader2 } from 'lucide-react'
import { format } from 'date-fns'
import { getImpuestoColorClasses } from '@/lib/impuestoColorResolver'
import { useAlertaVencimientoForm, getFieldError, type AlertaVencimientoFormValues } from './forms'
import type { AlcanceAlerta } from '@/types/vencimientos'
import { useStore } from '@tanstack/react-form'
import { FormDialogContent } from '@/components/ui/form-dialog-content'

interface VencimientoParaAlerta {
  id: string | null
  tipo_impuesto: string | null
  cliente_nombre: string | null
  periodo: string | null
  fecha_vencimiento: string | null
  impuesto_id?: string | null
}

interface ProgramarAlertaDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  vencimiento: VencimientoParaAlerta | null
  /** Id de la alerta ya programada sobre este vencimiento, si la hay */
  alertaExistenteId?: string | null
  onConfirmar: (datos: AlertaVencimientoFormValues) => Promise<void>
  onQuitar?: (alertaId: string) => Promise<void>
}

const OPCIONES_ALCANCE: Array<{ valor: AlcanceAlerta; titulo: string; detalle: string }> = [
  {
    valor: 'solo_proximo',
    titulo: 'Solo este vencimiento',
    detalle: 'Avisa una vez y la alerta se apaga.',
  },
  {
    valor: 'todos_los_siguientes',
    titulo: 'Todos los siguientes',
    detalle: 'Avisa en cada período de este impuesto para este cliente.',
  },
]

export function ProgramarAlertaDialog({
  open,
  onOpenChange,
  vencimiento,
  alertaExistenteId,
  onConfirmar,
  onQuitar,
}: ProgramarAlertaDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { form } = useAlertaVencimientoForm({
    onSubmit: async (value: AlertaVencimientoFormValues) => {
      setIsSubmitting(true)
      try {
        await onConfirmar(value)
        form.reset()
        onOpenChange(false)
      } catch (error) {
        console.error('Error al programar alerta:', error)
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const isDirty = useStore(form.store, (s) => s.isDirty)

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) form.reset()
    onOpenChange(newOpen)
  }

  const handleQuitar = async () => {
    if (!alertaExistenteId || !onQuitar) return
    setIsSubmitting(true)
    try {
      await onQuitar(alertaExistenteId)
      onOpenChange(false)
    } catch (error) {
      console.error('Error al quitar alerta:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!vencimiento) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <FormDialogContent
        isDirty={isDirty}
        onDiscard={() => handleOpenChange(false)} className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-emerald-600" />
            Programar alerta
          </DialogTitle>
          <DialogDescription>
            Te vamos a avisar a vos, en la campanita y por email si lo tenés activado.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 p-3 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            {vencimiento.tipo_impuesto && (
              <Badge variant="outline" className={getImpuestoColorClasses(vencimiento.tipo_impuesto)}>
                {vencimiento.tipo_impuesto}
              </Badge>
            )}
            <span className="text-sm font-medium">{vencimiento.cliente_nombre}</span>
          </div>
          <div className="text-sm text-muted-foreground">
            <span>Periodo: {vencimiento.periodo}</span>
            {vencimiento.fecha_vencimiento && (
              <>
                <span className="mx-2">|</span>
                <span>
                  Vencimiento: {format(new Date(vencimiento.fecha_vencimiento), 'dd/MM/yyyy')}
                </span>
              </>
            )}
          </div>
        </div>

        {alertaExistenteId && (
          <p className="text-sm text-muted-foreground">
            Este vencimiento ya tiene una alerta programada. Podés quitarla o agregar otra con
            distinta anticipación.
          </p>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-4"
        >
          <form.Field name="dias_antes">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Avisarme con esta anticipación (días) *</Label>
                <Input
                  id={field.name}
                  type="number"
                  min={0}
                  max={60}
                  value={field.state.value ?? ''}
                  onChange={(e) => {
                    const val = e.target.value
                    field.handleChange(val === '' ? 0 : parseInt(val, 10))
                  }}
                  onBlur={field.handleBlur}
                />
                <p className="text-xs text-muted-foreground">
                  0 avisa el mismo día del vencimiento.
                </p>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="alcance">
            {(field) => (
              <div className="space-y-2">
                <Label>Alcance *</Label>
                <div className="grid gap-2">
                  {OPCIONES_ALCANCE.map((opcion) => (
                    <button
                      key={opcion.valor}
                      type="button"
                      onClick={() => field.handleChange(opcion.valor)}
                      className={`text-left rounded-lg border p-3 transition-colors ${
                        field.state.value === opcion.valor
                          ? 'border-emerald-600 bg-emerald-50 dark:bg-emerald-950/30'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <span className="block text-sm font-medium">{opcion.titulo}</span>
                      <span className="block text-xs text-muted-foreground">{opcion.detalle}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </form.Field>

          <DialogFooter className="gap-2 sm:justify-between">
            {alertaExistenteId && onQuitar ? (
              <Button
                type="button"
                variant="outline"
                onClick={handleQuitar}
                disabled={isSubmitting}
              >
                <BellOff className="h-4 w-4 mr-2" />
                Quitar alerta
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Programar
              </Button>
            </div>
          </DialogFooter>
        </form>
      </FormDialogContent>
    </Dialog>
  )
}
